// 思考の癖メタ認知（重複・類似メモから抽出）
// 既存埋め込みベクトルでメモの類似ペアを検出 → 上位 N ペアを AI に渡してジャーナル風レポートを生成。
import {
  escapeHtml,
  screenHead,
  showScreen,
  showLoading,
  hideLoading,
  toast,
  renderHistorySection,
  bindHistoryHandlers,
} from '../ui.js';
import { fetchThoughtPatterns, fetchEmbeddings, fetchAllNotesParallel } from '../api.js';
import {
  getAllBooks,
  upsertBooks,
  getAllEmbeddings,
  saveEmbedding,
  saveAiHistory,
  getAiHistoryByType,
} from '../cache.js';

const TOP_PAIRS = 10;
// 類似度の下限（低すぎるペアは「ただ無関係」として除外）
const SIMILARITY_THRESHOLD = 0.55;

export async function showThoughtPatternsScreen() {
  const histItems = await getAiHistoryByType('thought-patterns');
  renderEntry(histItems);
}

function renderEntry(histItems) {
  const html = `
    ${screenHead('思考の癖')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 20px;">
      意味的に近いメモのペアを抽出し、その背後にある「あなたが繰り返し戻ってくる思考の癖」を
      AI がジャーナル風に深掘りします。<br/>
      埋め込みベクトルが必要なため、初回は時間がかかります。
    </p>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>思考の癖を抽出する</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: claude-opus-5-5 + text-embedding-3-small · 1 回あたり 約 5-15 円
    </p>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('thought-patterns', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-go').addEventListener('click', runAnalysis);
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => renderResult(entry.payload),
    () => showThoughtPatternsScreen()
  );
}

function quickHash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return String(h);
}

function buildSource(book) {
  const note = (book.note || '').trim();
  if (!note) return '';
  return `${book.name}\n${book.author || ''}\n\n${note}`.slice(0, 7000);
}

async function ensureEmbeddings(books) {
  const have = await getAllEmbeddings();
  const haveMap = new Map(have.map((e) => [e.bookId, e]));
  const need = [];
  for (const b of books) {
    const src = buildSource(b);
    if (!src) continue;
    const hash = quickHash(src);
    const cached = haveMap.get(b.id);
    if (!cached || cached.sourceHash !== hash) {
      need.push({ book: b, src, hash });
    }
  }
  if (need.length === 0) return;
  showLoading(`書籍を埋め込み中… (0/${need.length})`);
  const texts = need.map((x) => x.src);
  const { vectors } = await fetchEmbeddings(texts);
  for (let i = 0; i < need.length; i++) {
    await saveEmbedding(need[i].book.id, vectors[i], need[i].hash);
    const ov = document.getElementById('overlay-msg');
    if (ov) ov.textContent = `書籍を埋め込み中… (${i + 1}/${need.length})`;
  }
  hideLoading();
}

function cosine(a, b) {
  let dot = 0;
  let na = 0;
  let nb = 0;
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  const denom = Math.sqrt(na) * Math.sqrt(nb);
  return denom ? dot / denom : 0;
}

async function runAnalysis() {
  const allBooks = await getAllBooks();
  const eligible = allBooks.filter((b) => (b.note || '').trim().length > 80);
  if (eligible.length < 5) {
    toast(`メモのある書籍が少なすぎます（${eligible.length} 冊、必要 5 冊以上）`);
    return;
  }
  // メモ補完
  const missing = eligible.filter((b) => !b.note).length;
  if (missing > 0) {
    showLoading(`書籍メモを補完中… (0/${missing})`);
    await fetchAllNotesParallel(eligible, (done, total) => {
      const ov = document.getElementById('overlay-msg');
      if (ov) ov.textContent = `書籍メモを補完中… (${done}/${total})`;
    });
    await upsertBooks(eligible);
    hideLoading();
  }

  try {
    await ensureEmbeddings(eligible);
  } catch (e) {
    hideLoading();
    console.error(e);
    toast('埋め込み生成に失敗しました');
    return;
  }

  showLoading('類似メモを照合中…');
  const embs = await getAllEmbeddings();
  const map = new Map(embs.map((e) => [e.bookId, e]));
  const idsWithEmb = eligible.filter((b) => map.has(b.id));
  // ペアごとに類似度
  const pairs = [];
  for (let i = 0; i < idsWithEmb.length; i++) {
    for (let j = i + 1; j < idsWithEmb.length; j++) {
      const a = idsWithEmb[i];
      const b = idsWithEmb[j];
      const score = cosine(map.get(a.id).vector, map.get(b.id).vector);
      if (score >= SIMILARITY_THRESHOLD) {
        pairs.push({
          score,
          books: [
            { name: a.name, author: a.author, note: a.note },
            { name: b.name, author: b.author, note: b.note },
          ],
        });
      }
    }
  }
  pairs.sort((a, b) => b.score - a.score);
  const topPairs = pairs.slice(0, TOP_PAIRS);
  hideLoading();

  if (topPairs.length < 2) {
    toast('似たメモのペアが少なすぎます。もう少しメモが蓄積されてからお試しください');
    return;
  }

  showLoading('AI が思考の癖を分析中…');
  try {
    const result = await fetchThoughtPatterns(topPairs);
    await saveAiHistory({
      type: 'thought-patterns',
      title: `思考の癖 (${topPairs.length}ペア)`,
      subtitle: (result.habits || []).slice(0, 2).map((h) => h.name).join(' / '),
      context: { pairCount: topPairs.length },
      payload: result,
    });
    renderResult(result);
  } catch (e) {
    console.error(e);
    toast('分析に失敗しました');
  } finally {
    hideLoading();
  }
}

export function renderFromHistory(entry) {
  renderResult(entry.payload);
}

function renderResult(data) {
  const habits = data.habits || [];
  const html = `
    ${screenHead('思考の癖')}
    <section class="tp-report">
      <p class="section-label">journal</p>
      <p class="tp-report__body">${escapeHtml(data.report || '').replace(/\n/g, '<br/>')}</p>
    </section>

    <p class="section-label" style="margin-top:32px;">habits</p>
    ${habits
      .map(
        (h, i) => `
      <article class="tp-habit">
        <p class="tp-habit__order">癖 ${i + 1}</p>
        <h3 class="tp-habit__name">${escapeHtml(h.name || '')}</h3>
        <p class="tp-habit__reflection">${escapeHtml(h.reflection || '')}</p>
        ${
          h.books?.length
            ? `<div class="tp-habit__books">${h.books
                .map((b) => `<span class="chip">${escapeHtml(b)}</span>`)
                .join('')}</div>`
            : ''
        }
      </article>
    `
      )
      .join('')}

    <button class="btn btn--block btn--ghost" id="btn-regen" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-refresh"/></svg>
      <span>再分析する</span>
    </button>
  `;
  const screen = showScreen('thought-patterns', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-regen').addEventListener('click', () => showThoughtPatternsScreen());
}
